import { useContext } from 'react';
import { toast } from 'react-toastify';

import { formatApiError } from '@common/errors/error-formatting.ts';
import { useCaptchaDataQuery, useLoginMagicLinkMutation } from '@common/cache';
import { LoginContext } from '../../context';
import { initialValues, sendMagicLinkEmailSchema } from '../schemas';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

type IFormShape = InferType<typeof sendMagicLinkEmailSchema>;

interface IUseSendMagicLinkEmailProps {
  setEmail: (email: string) => void;
}

export const useSendMagicLinkEmail = ({
  setEmail,
}: IUseSendMagicLinkEmailProps) => {
  const { handlePreviousStep } = useContext(LoginContext);
  const {
    data = { id: '', svg: '' },
    refetch,
    isFetching,
  } = useCaptchaDataQuery();

  const { mutateAsync } = useLoginMagicLinkMutation();

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(sendMagicLinkEmailSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setValue,
  } = formMethods;

  const submitHandler = async (values: IFormShape) => {
    try {
      await mutateAsync({ ...values, captcha_id: data.id });

      setEmail(values.email);
      reset({ email: values.email });
      toast.success('Email successfully sent');
    } catch (error) {
      toast.error(formatApiError(error));
      setValue('captcha_answer', '');
    } finally {
      refetch();
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleRefresh = () => {
    refetch();
  };

  return {
    handleRefresh,
    handlePreviousStep,
    loading: isFetching || isSubmitting,
    onSubmit,
    formMethods,
    svg: data.svg,
  };
};
